import { format, isAfter, isBefore, parse, startOfDay, startOfMonth } from "date-fns";
import { FLIGHT_LIMITS } from "@/feature/flight/search/types/constants";
import type {
  TAirport,
  TFlightSegment,
  TPassengerCounts,
  TTripType,
} from "@/feature/flight/search/types/search";

// ============================================================================
// Date Utils
// ============================================================================

export const DateUtils = {
  getToday(): Date {
    return startOfDay(new Date());
  },

  isBefore(date: Date, compareDate: Date): boolean {
    return isBefore(date, compareDate);
  },

  isBeforeToday(date: Date): boolean {
    return isBefore(date, DateUtils.getToday());
  },

  getDefaultMonth(minDate?: Date, selectedValue?: Date): Date | undefined {
    if (selectedValue) return selectedValue;
    if (minDate) {
      const today = DateUtils.getToday();
      const minDateMonth = startOfMonth(minDate);
      const todayMonth = startOfMonth(today);
      if (isAfter(minDateMonth, todayMonth)) return minDate;
    }
  },

  formatDate(date: Date, formatStr: string = "yyyy-MM-dd"): string {
    return format(date, formatStr);
  },

  parseDate(dateStr: string, formatStr: string = "yyyy-MM-dd"): Date {
    return parse(dateStr, formatStr, new Date());
  }
};

// ============================================================================
// Flight Form Logic
// ============================================================================

export const FlightFormHelper = {
  createEmptyFlightSegment(): TFlightSegment {
    return {
      from: { iata_code: "", name: "", city_name: "", country: "" },
      to: { iata_code: "", name: "", city_name: "", country: "" },
    };
  },

  getInitialFlights(tripType: TTripType): TFlightSegment[] {
    const empty = FlightFormHelper.createEmptyFlightSegment;
    switch (tripType) {
      case "oneway": return [empty()];
      case "roundtrip": return [empty(), empty()];
      case "multicity": return [empty(), empty()];
      default: return [empty()];
    }
  },

  updateFlightsOnTripTypeChange(
    newTripType: TTripType,
    currentFlights: TFlightSegment[]
  ): TFlightSegment[] {
    const emptyAirport: TAirport = { iata_code: "", name: "", city_name: "", country: "" };
    const firstFlight = currentFlights[0] || { from: emptyAirport, to: emptyAirport };

    const updatedFlights = firstFlight.departDate
      ? new Date(firstFlight.departDate.getTime() + 2 * 24 * 60 * 60 * 1000)
      : undefined

    switch (newTripType) {
      case "oneway":
        return [{ ...firstFlight }];
      case "roundtrip":
        return [
          { ...firstFlight },
          {
            from: firstFlight.to || emptyAirport,
            to: firstFlight.from || emptyAirport,
            departDate: updatedFlights
          },
        ];
      case "multicity":
        return [{ ...firstFlight }, {
          from: firstFlight.to || emptyAirport, to: emptyAirport, departDate: updatedFlights
        }];
      default:
        return FlightFormHelper.getInitialFlights(newTripType);
    }
  },

  getTotalPassengers(passengers: TPassengerCounts): number {
    return passengers.adults + passengers.children + passengers.infants;
  },

  canAddPassenger(
    currentTotal: number,
    maxPassengers: number = FLIGHT_LIMITS.MAX_PASSENGERS
  ): boolean {
    return currentTotal < maxPassengers;
  },

  canAddInfant(
    currentInfants: number,
    currentAdults: number,
    maxPassengers: number = FLIGHT_LIMITS.MAX_PASSENGERS,
    currentTotal: number
  ): boolean {
    return (
      currentInfants < currentAdults && FlightFormHelper.canAddPassenger(currentTotal, maxPassengers)
    );
  },

  canAddTFlightSegment(
    currentFlights: TFlightSegment[],
    maxFlights: number = FLIGHT_LIMITS.MAX_FLIGHTS
  ): boolean {
    return currentFlights.length < maxFlights;
  },

  createNextTFlightSegment(currentFlights: TFlightSegment[]): TFlightSegment {
    const emptyAirport: TAirport = { iata_code: "", name: "", city_name: "", country: "" };
    const lastFlight = currentFlights[currentFlights.length - 1];

    const fromAirport = lastFlight?.to?.iata_code ? lastFlight.to : emptyAirport;
    return {
      from: fromAirport,
      to: emptyAirport,
      departDate: lastFlight?.departDate
        ? new Date(lastFlight.departDate.getTime() + 3 * 24 * 60 * 60 * 1000)
        : undefined
    };
  },

  canRemoveTFlightSegment(
    currentFlights: TFlightSegment[],
    minFlights: number = FLIGHT_LIMITS.MIN_MULTI_CITY_FLIGHTS
  ): boolean {
    return currentFlights.length > minFlights;
  },

  getMinReturnDate(departDate?: Date): Date | null {
    return departDate || DateUtils.getToday();
  },

  formatPassengerText(total: number): string {
    return `${total} ${total === 1 ? "Passenger" : "Passengers"}`;
  }
};

// ============================================================================
// Passenger Logic
// ============================================================================

export const PassengerHelper = {
  updateCount(
    type: keyof TPassengerCounts,
    currentValue: number,
    increment: boolean,
    allPassengers: TPassengerCounts
  ): number | null {
    const newValue = increment ? currentValue + 1 : currentValue - 1;
    const totalPassengers = FlightFormHelper.getTotalPassengers(allPassengers);

    if (type === "adults" && newValue < FLIGHT_LIMITS.MIN_ADULTS) return null;
    if (newValue < 0) return null;
    if (increment && totalPassengers >= FLIGHT_LIMITS.MAX_PASSENGERS) return null;

    if (type === "infants" && increment) {
      if (newValue > allPassengers.adults) return null;
    }

    return newValue;
  },

  getPassengerDescription(type: keyof TPassengerCounts) {
    const descriptions = {
      adults: { title: "Adults", subtitle: "12+ years" },
      children: { title: "Children", subtitle: "2-11 years" },
      infants: { title: "Infants", subtitle: "Under 2 years" },
    };
    return descriptions[type];
  }
};

// ============================================================================
// Flight Date Logic
// ============================================================================

export const FlightDateHelper = {
  getSubsequentFlightsToClear(
    flights: TFlightSegment[],
    fromIndex: number,
    newDate: Date
  ): { clearedCount: number; indicesToClear: number[] } {
    const indicesToClear: number[] = [];
    let previousDate: Date = newDate;
    for (let i = fromIndex + 1; i < flights.length; i++) {
      const currentDate = flights[i]?.departDate;
      if (!currentDate) break;
      if (isBefore(currentDate, previousDate)) {
        // clear this and EVERYTHING after
        for (let j = i; j < flights.length; j++) {
          if (flights[j]?.departDate) {
            indicesToClear.push(j);
          }
        }
        break;
      }
      previousDate = currentDate;
    }
    return {
      clearedCount: indicesToClear.length,
      indicesToClear,
    };
  },

  getClearedDatesToastMessage(clearedCount: number) {
    if (clearedCount === 0) return null;
    return {
      message: clearedCount === 1 ? "Date cleared" : `${clearedCount} dates cleared`,
      description: "Please select new dates for the subsequent flights.",
    };
  },

  isDateDisabled(date: Date, minDate?: Date | null): boolean {
    if (!minDate) return false;
    return isBefore(date, minDate);
  },

  getMinDateForFlight(
    previousFlightDate: Date | undefined
  ): Date {
    return previousFlightDate ?? DateUtils.getToday();
  },

  reorderFlights(
    flights: TFlightSegment[],
    fromIndex: number,
    toIndex: number,
  ): TFlightSegment[] {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= flights.length ||
      toIndex >= flights.length
    ) {
      return flights;
    }

    const reorderedFlights = [...flights];
    const [movedFlight] = reorderedFlights.splice(fromIndex, 1);

    if (!movedFlight) {
      return flights;
    }

    reorderedFlights.splice(toIndex, 0, movedFlight);
    return reorderedFlights;
  },

  getInvalidMultiCityDateIndices(flights: TFlightSegment[]): number[] {
    const indicesToClear: number[] = [];
    let previousDate: Date | undefined;

    for (let index = 0; index < flights.length; index += 1) {
      const currentDate = flights[index]?.departDate;
      if (!currentDate) {
        continue;
      }

      if (previousDate && isBefore(currentDate, previousDate)) {
        for (let clearIndex = index; clearIndex < flights.length; clearIndex += 1) {
          if (flights[clearIndex]?.departDate) {
            indicesToClear.push(clearIndex);
          }
        }
        break;
      }

      previousDate = currentDate;
    }

    return indicesToClear;
  }
};



